import { defineStore } from 'pinia';
import { createError, useNuxtApp } from 'nuxt/app';
import { computed, ref } from 'vue';
import { isAxiosError, type AxiosResponse } from 'axios';
import { categorySlugify, type CategoryType } from '~~/common/utils/category';
import { setErrorNotification } from '~~/common/utils/helper';
import { AW_AX_CACHE_SEGMENTATION } from '~~/common/plugins/axios/awAxCache';
import { useDeliveryStore } from '~~/shop/app/stores/delivery';
import { useAwCacheKeyStore } from '~~/common/stores/awCacheKey';
import ObjectCache from '~~/common/utils/ObjectCache';

// The slugified category tree, augmented with a FE-only cache marker (`awMeta`).
type CategoryTree = CategoryType & { awMeta?: { cacheKey?: string } };

// GET /tree/{id} resolves a category on 200, but a body carrying a `message` on a 307
// redirect — it's one or the other, so the cached response body is a union.
interface CategoryRedirectBody {
  message: string;
}
type CategoryByIdBody = CategoryType | CategoryRedirectBody;

function isRedirectBody (data: CategoryByIdBody | undefined): data is CategoryRedirectBody {
  return !!data && 'message' in data && typeof data.message === 'string';
}

// Two separate caches because the two endpoints resolve different shapes:
// /tree/0 caches the tree data, /tree/{id} (fetchCategoryById) caches the full axios
// response — including the in-flight promise, so concurrent callers dedupe onto one request.
const treeCache = new ObjectCache<Promise<CategoryTree>>('category', '__');
const categoryByIdCache = new ObjectCache<Promise<AxiosResponse<CategoryByIdBody>>>('category', '__');

export const useCategoryStore = defineStore('category', () => {
  const nuxtApp = useNuxtApp();

  const deliveryStore = useDeliveryStore();
  const awCacheKeyStore = useAwCacheKeyStore();

  const categories = ref<CategoryTree | null>(null);
  const category = ref<CategoryType | null>(null);
  const treeId = ref<number | null>(null);
  const categoryIdBeforeVisitProductPage = ref<number | false>(false);

  function setCategories (newCategories: CategoryTree | null) {
    categories.value = newCategories ? categorySlugify(newCategories) : null;
  }

  function setTreeId (newTreeId: number | null | undefined) {
    if (typeof newTreeId === 'number') {
      treeId.value = newTreeId;
    } else {
      treeId.value = null;
    }
  }

  function setCategory (newCategory: CategoryType | null) {
    category.value = newCategory;
  }

  function setCategoryIdBeforeVisitProductPage (payload: number | false) {
    categoryIdBeforeVisitProductPage.value = payload;
  }

  async function fetchCategories () {
    const { $api, $logger, $i18n } = nuxtApp;

    await awCacheKeyStore.ensureCacheSegmentation();

    const postCode = deliveryStore.getPostCode;
    const cacheKey = ['category', '0', $i18n.locale.value, awCacheKeyStore.cacheKeySegmentation || postCode];
    try {
      const getCacheKeyString = () => cacheKey.join('__');
      if (categories.value?.awMeta?.cacheKey !== getCacheKeyString()) {
        treeCache.initCache(cacheKey, async () => {
          const axiosResponse = await $api.get<CategoryTree>('/tree/0', { awAxCache: AW_AX_CACHE_SEGMENTATION });
          const response = axiosResponse.data;
          const config = axiosResponse.config;
          if ('segmentationCacheKeyChange' in config && typeof config.segmentationCacheKeyChange === 'string') {
            treeCache.deleteCache(cacheKey);
            cacheKey[3] = config.segmentationCacheKeyChange;
            treeCache.initCache(cacheKey, async () => {
              return response;
            });
          }
          if (!response.awMeta) {
            response.awMeta = {};
          }
          response.awMeta.cacheKey = getCacheKeyString();
          return response;
        });
        const newCategories = await treeCache.getCache(cacheKey)?.value;
        setCategories(newCategories ?? null);
      }
    } catch (error) {
      $logger.error(error);
      setErrorNotification({ nuxtApp, error });
      setCategories(null);
    }
  }

  async function fetchCategoryById (id: number | string) {
    const { $api, $awt, $logger, $i18n } = nuxtApp;

    await awCacheKeyStore.ensureCacheSegmentation();

    const postCode = deliveryStore.getPostCode;
    const cacheKey = ['category', String(id), $i18n.locale.value, awCacheKeyStore.cacheKeySegmentation || postCode];

    let is404Redirect = false;
    try {
      categoryByIdCache.initCache(cacheKey, () => $api.get<CategoryByIdBody>(`/tree/${id}`, { awAxCache: AW_AX_CACHE_SEGMENTATION }));
      const result = await categoryByIdCache.getCache(cacheKey)?.value;

      if (result?.status === 307) {
        if (isRedirectBody(result.data)) {
          setErrorNotification({
            nuxtApp,
            error: {
              response: {
                data: {
                  title: result.data.message,
                },
              },
            },
          });
        }
        is404Redirect = true;
      }
      const newCategory = result && !isRedirectBody(result.data) ? result.data : null;
      category.value = newCategory;
      setTreeId(newCategory?.id);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        setErrorNotification({
          nuxtApp,
          error: {
            response: {
              data: {
                title: $awt('aw.category.notfound'),
              },
            },
          },
        });
        is404Redirect = true;
      } else {
        setErrorNotification({ nuxtApp, error });
        $logger.error(error);
        throw error;
      }
    }
    if (is404Redirect) {
      throw createError({
        statusCode: 404,
        fatal: true,
      });
    }
  }

  function extractPromotionContentFromObject (
    parentObject?: {
      promotionContentType?: string | null;
      promotionContentId?: number | null;
    } | null,
  ) {
    return {
      type: parentObject?.promotionContentType,
      id: parentObject?.promotionContentId,
    };
  }

  function getCategoryFromTreeById ({
    treeId: treeIdParam,
  }: {
    treeId: number | null;
  }) {
    const tree = categories.value;
    if (typeof treeIdParam === 'number' && tree?.children) {
      return findByFunc(tree, ({ id }) => id === treeIdParam, Infinity) || null;
    } else {
      return null;
    }
  }

  // getters
  const getCategoryFromTree = computed(() => {
    return getCategoryFromTreeById({ treeId: treeId.value });
  });


  return {
    categories,
    category,
    treeId,
    categoryIdBeforeVisitProductPage,
    setCategories,
    setTreeId,
    setCategory,
    setCategoryIdBeforeVisitProductPage,
    fetchCategories,
    fetchCategoryById,
    extractPromotionContentFromObject,
    getCategoryFromTree,
    getCategoryFromTreeById,
  };
});

function findByFunc (
  {
    children: arr,
  }: {
    children: CategoryType[];
  },
  searchFun: (category: CategoryType) => boolean,
  maxDepth: number,
  currentDepth = 0,
): CategoryType | undefined {
  const result = arr.find(searchFun);
  if (!result && currentDepth <= maxDepth) {
    for (const arrChild of arr) {
      const subResult = findByFunc(arrChild, searchFun, maxDepth, currentDepth + 1);
      if (subResult) {
        return subResult;
      }
    }
  }
  return result;
}
